import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface FlowProtectionOptions {
    /** Session storage key to check for */
    storageKey: string;
    /** Where to redirect if accessed directly without proper flow */
    fallbackPath: string;
    /** If true, clears the session storage after successful access */
    clearOnAccess?: boolean;
    /** If true, allows authenticated users with incomplete profiles to bypass flow protection (for onboarding pages) */
    allowIncompleteProfile?: boolean;
}

interface FlowProtectionResult {
    isValidAccess: boolean | null;
    flowData: Record<string, unknown> | null;
}

/**
 * Checks if user is authenticated with an incomplete profile
 * Used to allow authenticated users to access onboarding pages
 */
function isAuthenticatedWithIncompleteProfile(): boolean {
    try {
        const authUserStr = localStorage.getItem('authUser');
        const authToken = localStorage.getItem('authToken');

        if (!authUserStr || !authToken) {
            return false;
        }

        const authUser = JSON.parse(authUserStr);
        const profileCompleted = authUser.profile_completed || authUser.profileCompleted;

        // User is authenticated and profile is NOT completed
        return profileCompleted === false;
    } catch {
        return false;
    }
}

/**
 * useFlowProtection Hook
 * Protects pages using Session Storage - persists on page refresh.
 * Best for pages where user might refresh (e.g., check email pages).
 *
 * Usage in source page:
 * ```tsx
 * // Set the flow data before navigating
 * sessionStorage.setItem('fromForgotPassword', JSON.stringify({ email: userEmail }));
 * navigate('/auth/check-inbox');
 * ```
 *
 * Usage in protected page:
 * ```tsx
 * const { isValidAccess, flowData } = useFlowProtection({
 *     storageKey: 'fromForgotPassword',
 *     fallbackPath: '/auth/login'
 * });
 * ```
 */
export function useFlowProtection({
    storageKey,
    fallbackPath,
    clearOnAccess = false,
    allowIncompleteProfile = false
}: FlowProtectionOptions): FlowProtectionResult {
    const navigate = useNavigate();
    const location = useLocation();
    const [isValidAccess, setIsValidAccess] = useState<boolean | null>(null);
    const [flowData, setFlowData] = useState<Record<string, unknown> | null>(null);

    useEffect(() => {
        // Check if authenticated user with incomplete profile should be allowed
        if (allowIncompleteProfile && isAuthenticatedWithIncompleteProfile()) {
            setIsValidAccess(true);
            setFlowData({ fromLogin: true });
            return;
        }

        // First check session storage
        const storedData = sessionStorage.getItem(storageKey);

        // Also check location state as fallback (for initial navigation)
        const locationState = location.state as Record<string, unknown> | null;

        if (storedData) {
            try {
                const parsedData = JSON.parse(storedData);
                setFlowData(parsedData);
                setIsValidAccess(true);

                if (clearOnAccess) {
                    sessionStorage.removeItem(storageKey);
                }
            } catch {
                // Invalid JSON, redirect
                setIsValidAccess(false);
                navigate(fallbackPath, { replace: true });
            }
        } else if (locationState && Object.keys(locationState).length > 0) {
            // If coming from navigation with state, store it in session storage
            sessionStorage.setItem(storageKey, JSON.stringify(locationState));
            setFlowData(locationState);
            setIsValidAccess(true);
        } else {
            // No valid access
            setIsValidAccess(false);
            navigate(fallbackPath, { replace: true });
        }
    }, [storageKey, fallbackPath, clearOnAccess, allowIncompleteProfile, navigate, location.state]);

    return { isValidAccess, flowData };
}

/**
 * Helper function to set flow data before navigation
 */
export function setFlowData(storageKey: string, data: Record<string, unknown>): void {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
}

/**
 * Helper function to clear flow data
 */
export function clearFlowData(storageKey: string): void {
    sessionStorage.removeItem(storageKey);
}

export default useFlowProtection;
